import { ReactNode } from "react";

import Box from "ds/components/Box";
import LinkButton from "ds/components/Button/LinkButton";
import useTypedContext from "hooks/useTypedContext";
import useTitle from "hooks/useTitle";
import { AccountContext } from "views/AccountWrapper";
import SearchInput from "components/SearchInput";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import ButtonIcon from "ds/components/ButtonIcon";
import { InfoCircle } from "components/icons/generated";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import DocumentationIconButton from "components/DocumentationIconButton";
import { getDocsUrl } from "utils/getDocsUrl";

import { FILTERS_ORDER_SETTINGS_KEY } from "./constants";
import useRolesListAnalytics from "./useRolesListAnalytics";
import { showRolesInfoDrawer } from "./InfoDrawer";

type RolesPageLayoutProps = {
  children: ReactNode;
  readOnly?: boolean;
};

const RolesPageLayout = ({ children, readOnly }: RolesPageLayoutProps) => {
  const { accountName } = useTypedContext(AccountContext);

  useTitle(`Organization Settings · Roles · ${accountName}`);

  const trackSegmentAnalyticsEvent = useRolesListAnalytics();

  const openInfoDrawer = () => {
    trackSegmentAnalyticsEvent("Info Click");
    showRolesInfoDrawer();
  };

  return (
    <>
      <PageInfoHeaderWrapper>
        <Box direction="row" align="center" gap="medium">
          <PageInfoHeaderTitle title="Roles" />
          <ButtonIcon onPress={openInfoDrawer} icon={InfoCircle} variant="ghost">
            Open info drawer
          </ButtonIcon>
        </Box>

        <PageInfoHeaderActions>
          <SearchInput
            placeholder={SEARCH_PLACEHOLDERS.ROLES}
            filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
          />

          <DocumentationIconButton
            href={getDocsUrl("/concepts/authorization/rbac-system")}
            tooltipText="Go to roles documentation"
          />

          {!readOnly && (
            <LinkButton
              analyticsPage={AnalyticsPageOrganization.OrganizationRoles}
              analyticsTitle="Create Role Clicked"
              variant="primary"
              to="../new/role"
            >
              Create role
            </LinkButton>
          )}
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>
      {children}
    </>
  );
};

export default RolesPageLayout;
